import { useState, useMemo, useEffect } from "react";
import { Container, styled, Box, CircularProgress } from "@mui/material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../styles/fonts/fonts.css';
import StyledButton from "components/button/StyledButton";
import { useNavigate } from 'react-router-dom';
import TurndownService from 'turndown';
import { useCreateMassEmailMutation } from '../../api/api';
import FormTextField from "components/form/FormTextField";

const turndownService = new TurndownService();
const fonts = ['helvetica-neue', 'sans-serif', 'serif', 'monospace'];
const Font = ReactQuill.Quill.import('formats/font');
Font.whitelist = fonts;
const modules = {
  toolbar: [
    [{ 'font': fonts }],
    [{ 'header': [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link'], // 'image' removed for now
    [{ 'color': [] }, { 'background': [] }],
    ['clean'],
  ]
};

const CustomEmailPage = () => {
  const [emailMessage, setEmailMessage] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [emailSent, setEmailSent] = useState<boolean>(false);

  const navigate = useNavigate();

  const [createMassEmail, { error: createMassEmailError, isLoading: isEmailSending }] = useCreateMassEmailMutation();
  
  const markdownEmailMessage = useMemo(() => turndownService.turndown(emailMessage), [emailMessage]);

  useEffect(() => {
    if (emailSent) setEmailSent(false);
  }, [emailMessage, subject]);

  const handleCancel = () => {
    navigate('/dashboard');
  }

  const handleSend = async () => {
    try {
      const response = await createMassEmail({ subject, message: markdownEmailMessage }).unwrap();
      if (response && 'id' in response && 'created_at' in response) {
        setEmailSent(true);
      }
    } catch (e) {
      console.error("Send mass email error: ", e);
    }
  }

  const handleOnChange = (value: string) => {
    setEmailMessage(value);
  }

  const getErrorMessage = (): string => {
    if (!createMassEmailError) return '';
    if (typeof createMassEmailError === 'object' && 'data' in createMassEmailError) {
      const errorData = createMassEmailError.data;
      if (typeof errorData === 'object' && errorData && 'detail' in errorData && errorData.detail) {
        return errorData.detail.toString();
      }
    }
    return 'Unexpected error';
  }

  return (
    <div>
      <StyledContainer>
        <div style={{ maxWidth: '100%', width: '100%', height: '100%', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 20, paddingTop: 1, marginTop: 20 }}>
          <h2>Send Custom Email to All Users</h2>
          <Box sx={{display: 'flex', flexDirection: 'column'}}>
            <p>Subject</p>
            <FormTextField 
                label="" 
                value={subject} 
                onChange={(e) => {setSubject(e.target.value)}}/>
            <p>Message</p>
          </Box>
          <ReactQuill value={emailMessage} onChange={handleOnChange} modules={modules} />
          {isEmailSending && (
            <Box marginTop={2} marginBottom={2}>
              <CircularProgress size="20px" />
            </Box>
          )}
          {createMassEmailError && (
            <Box>
              <p>
                Mass email error: {getErrorMessage()}
              </p>
            </Box>
          )}
          {!createMassEmailError && emailSent && (
            <Box>
              <p>
                Email sent successfully.
              </p>
            </Box>
          )}
          <Box sx={{
            display: 'flex',
            maxWidth: '20vw',
            marginTop: '20px',
          }}>
            <StyledButton label="Send" type='filled' disabled={!subject || !markdownEmailMessage} onClick={handleSend}></StyledButton>
            <StyledButton label="Cancel" type='outlined' onClick={handleCancel}></StyledButton>
          </Box>
        </div>
      </StyledContainer>
    </div>
  );
};

export default CustomEmailPage;

const StyledContainer = styled(Container)({
  "& .sectionTitle": {
    textDecoration: "underline",
    fontWeight: "bold",
  },
});
